import { MARKETPLACE_PORTAL_IDLE_AD_ORDER_ID } from '@lucimark/shared/constants/marketplace'

/** Playlist `metadata.source` for auto-managed Lucimark Book CPS fulfillment copies. */
export const PORTAL_FULFILLMENT_PLAYLIST_SOURCE = 'marketplace_portal_fulfillment' as const

export interface TPortalFulfillmentPlaylistMetadata extends Record<string, unknown> {
  source: typeof PORTAL_FULFILLMENT_PLAYLIST_SOURCE
  portalAdOrderId: string
  portalPlayerId: string
}

export interface TPortalFulfillmentPlaylistDocLike {
  title?: string | null
  metadata?: Record<string, unknown> | null
}

export function buildPortalFulfillmentPlaylistMetadata(
  portalAdOrderId: string,
  playerId: string
): TPortalFulfillmentPlaylistMetadata {
  return {
    source: PORTAL_FULFILLMENT_PLAYLIST_SOURCE,
    portalAdOrderId,
    portalPlayerId: playerId
  }
}

export function isPortalFulfillmentPlaylistMetadata(
  metadata: Record<string, unknown> | null | undefined
): metadata is TPortalFulfillmentPlaylistMetadata {
  return metadata?.source === PORTAL_FULFILLMENT_PLAYLIST_SOURCE
    && typeof metadata.portalAdOrderId === 'string'
    && metadata.portalAdOrderId.trim() !== ''
    && typeof metadata.portalPlayerId === 'string'
    && metadata.portalPlayerId.trim() !== ''
}

export function portalFulfillmentIdlePlaylistTitle(playerId: string): string {
  return `Book idle ${playerId.slice(0, 8)}`
}

export function portalFulfillmentOrderPlaylistTitle(portalAdOrderId: string): string {
  return `Book ${portalAdOrderId.slice(0, 8)}`
}

/** Matches idle-house fulfillment playlists (metadata or legacy title). */
export function playlistMatchesPortalIdleFulfillment(
  playlist: TPortalFulfillmentPlaylistDocLike,
  playerId: string
): boolean {
  const meta = playlist.metadata
  if (isPortalFulfillmentPlaylistMetadata(meta)) {
    return meta.portalPlayerId === playerId
      && meta.portalAdOrderId === MARKETPLACE_PORTAL_IDLE_AD_ORDER_ID
  }
  return (playlist.title?.trim() ?? '') === portalFulfillmentIdlePlaylistTitle(playerId)
}

/** Matches paid-order fulfillment playlists for one screen (metadata or legacy title). */
export function playlistMatchesPortalOrderFulfillment(
  playlist: TPortalFulfillmentPlaylistDocLike,
  portalAdOrderId: string,
  playerId: string
): boolean {
  const meta = playlist.metadata
  if (isPortalFulfillmentPlaylistMetadata(meta)) {
    return meta.portalPlayerId === playerId
      && meta.portalAdOrderId === portalAdOrderId
  }
  return (playlist.title?.trim() ?? '') === portalFulfillmentOrderPlaylistTitle(portalAdOrderId)
}

export function isAutoManagedPortalFulfillmentPlaylistDoc(
  playlist: TPortalFulfillmentPlaylistDocLike
): boolean {
  if (isPortalFulfillmentPlaylistMetadata(playlist.metadata)) {
    return true
  }
  const title = playlist.title?.trim() ?? ''
  return title.startsWith('Book idle ') || title.startsWith('Book ')
}
